import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Square } from 'lucide-react';
import { useRealtimeVoice } from '../hooks/useRealtimeVoice';
import { turnNarrationText } from '../lib/narration';
import { shouldStartPtt, shouldStopPtt } from '../lib/voicePushToTalkKey';
import { postDevLog } from '../lib/realtimeVoice';
import { api } from '../api/client';
import type { AgentState } from '../api/types';
import { useServiceAvailable } from '../hooks/useServiceOrigin';

// The persistent AgentOS narrator lives ONCE at the app root (Alex' continuous-
// flow directive 2026-06-24): the WebRTC session + audio survive view changes,
// so navigating between agents is a focus shift, never a teardown. The provider
// also owns AMBIENT-ROAM: when the focused agent goes idle and the operator has
// enabled it, the narrator quietly follows whichever other agent is active.

export type AmbientMode = 'focus' | 'ambient';
const AMBIENT_KEY = 'cloud-voice-ambient';
const ROAM_POLL_MS = 2500;

const readAmbient = (): AmbientMode =>
  (typeof localStorage !== 'undefined' && localStorage.getItem(AMBIENT_KEY) === 'ambient') ? 'ambient' : 'focus';

const isActive = (s?: AgentState): boolean => s?.state === 'thinking' || s?.state === 'tool';

// Pick a background agent to roam to: keep the current one while it's still
// active (sticky — don't flip-flop), else the first other active agent. `scope`
// (the operator's visible/filtered list) restricts the candidates; an empty
// scope = no restriction (e.g. before the list has reported).
function pickRoamTarget(agentStates: Record<string, AgentState>, base: string | null, current: string | null, scope: string[]): string | null {
  const inScope = (name: string) => scope.length === 0 || scope.includes(name);
  if (current && isActive(agentStates[current]) && inScope(current)) return current;
  for (const [name, st] of Object.entries(agentStates)) {
    if (name === base) continue;
    if (!inScope(name)) continue;
    if (isActive(st)) return name;
  }
  return null;
}

type VoiceApi = ReturnType<typeof useRealtimeVoice>;
interface VoiceContextValue extends VoiceApi {
  /** Klartext-Name des Modus der LAUFENDEN Sitzung. Bewusst beim Start
   *  eingefroren: die Auswahl kann sich danach ändern, die laufende Sitzung
   *  nicht — sie wird beim Verbindungsaufbau festgelegt. */
  runningModeLabel: string;
  setBaseFocus: (name: string) => void;
  ambientMode: AmbientMode;
  setAmbientMode: (m: AmbientMode) => void;
  isRoaming: boolean;
  roamTarget: string | null;
  canUseVoice: boolean;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

export function VoiceProvider({
  children,
  agentStates,
  roamScope = [],
  realtimeEnabled = false,
  realtimeEndpoint = null,
}: {
  children: ReactNode;
  agentStates: Record<string, AgentState>;
  roamScope?: string[];
  realtimeEnabled?: boolean;
  realtimeEndpoint?: string | null;
}) {
  // Talkback (Step 2): opt-in via Settings (`cloud-voice-talkback`, off). When on,
  // the NEXT voice start mints `talkback-enabled` (adds propose_to_agent).
  const [talkback, setTalkback] = useState<boolean>(() => localStorage.getItem('cloud-voice-talkback') === 'on');
  useEffect(() => {
    const h = () => setTalkback(localStorage.getItem('cloud-voice-talkback') === 'on');
    window.addEventListener('cloud-voice-talkback-change', h);
    window.addEventListener('cloud-prefs-pulled', h);
    return () => {
      window.removeEventListener('cloud-voice-talkback-change', h);
      window.removeEventListener('cloud-prefs-pulled', h);
    };
  }, []);

  const voice = useRealtimeVoice(realtimeEndpoint, talkback);
  // Access control (#1220): the toggle only exists when the user is enabled AND
  // a server-resolved endpoint is present. NOT a security boundary (that's the
  // AiApi grant at mint) — just the UX gate / no speculative UI.
  // Instanz-Gate (77eb0c9): deklariert die Instanz die Sprachschicht mit
  // null, existiert der Dienst dort nicht — kein Knopf, keine Fehlerflut.
  const aiAvailable = useServiceAvailable('ai');
  const canUseVoice = aiAvailable && realtimeEnabled && !!realtimeEndpoint;

  // The "home" agent the operator is viewing — the narrator returns here when no
  // roam is active. A ref mirrors it so callers can poke it every render cheaply.
  const baseFocusRef = useRef<string | null>(null);
  const [baseFocus, setBaseFocusState] = useState<string | null>(null);
  const [roamTarget, setRoamTarget] = useState<string | null>(null);
  // Roam feed dedup, persisted across effect re-runs (target flaps when the home
  // agent flickers active/idle); reset only on a real target change.
  const roamFeedRef = useRef<{ target: string | null; last: string; primed: boolean }>({ target: null, last: '', primed: false });
  // Ambient-Roam is DISARMED at session start until the FOCUSED (home) agent has
  // been narrated once — so the operator always hears their agent's recap first,
  // THEN ambient may roam to an active background agent (Alex 2026-06-24: "ich bin
  // in CloudV2 fokussiert, am Anfang erwarte ich CloudV2, nicht Edera").
  const [roamArmed, setRoamArmed] = useState(false);
  const [ambientMode, setAmbientModeState] = useState<AmbientMode>(readAmbient);

  const setBaseFocus = useCallback((name: string) => {
    if (baseFocusRef.current === name) return; // unchanged → never disturb an active roam
    baseFocusRef.current = name;
    setBaseFocusState(name);
    setRoamTarget(null); // genuine navigation cancels roaming
  }, []);

  // Start the narrator on an agent + make it the home focus.
  // Beim Start eingefroren — danach darf die Auswahl wandern, die Anzeige der
  // laufenden Sitzung nicht.
  const [runningModeLabel, setRunningModeLabel] = useState('Sprachsitzung');
  const start = useCallback((name: string) => {
    let m = 'narrator';
    try { m = localStorage.getItem('cloud-voice-mode') || 'narrator'; } catch { /* ignore */ }
    setRunningModeLabel(
      m === 'arcturian' ? 'Arcturian'
        : m === 'talkback' ? 'Talkback'
        : m === 'transparent' ? 'Gespräch'
        : 'AgentOS-Narrator',
    );
    baseFocusRef.current = name;
    setBaseFocusState(name);
    setRoamTarget(null);
    return voice.start(name);
  }, [voice]);

  const setAmbientMode = useCallback((m: AmbientMode) => {
    try { localStorage.setItem(AMBIENT_KEY, m); } catch { /* private mode */ }
    setAmbientModeState(m);
    // Keep Settings (outside this provider) in sync when toggled from the voice bar.
    window.dispatchEvent(new CustomEvent('cloud-voice-ambient-change', { detail: m }));
  }, []);
  // Re-read when Settings toggles it (Settings lives outside this provider) or
  // when cross-device prefs sync lands.
  useEffect(() => {
    const h = () => setAmbientModeState(readAmbient());
    window.addEventListener('cloud-voice-ambient-change', h);
    window.addEventListener('cloud-prefs-pulled', h);
    return () => {
      window.removeEventListener('cloud-voice-ambient-change', h);
      window.removeEventListener('cloud-prefs-pulled', h);
    };
  }, []);

  // Dev-mode narration-log upload (Alex 2026-06-24): when on, debounce-upload the
  // full transcript (upsert by voice_session_id) so CloudV2 can analyse what
  // reached the narrator. Off by default; Settings toggles it via the event.
  const [devlog, setDevlog] = useState<boolean>(() => localStorage.getItem('cloud-voice-devlog') === 'on');
  useEffect(() => {
    const h = () => setDevlog(localStorage.getItem('cloud-voice-devlog') === 'on');
    window.addEventListener('cloud-voice-devlog-change', h);
    window.addEventListener('cloud-prefs-pulled', h);
    return () => {
      window.removeEventListener('cloud-voice-devlog-change', h);
      window.removeEventListener('cloud-prefs-pulled', h);
    };
  }, []);
  useEffect(() => {
    if (!devlog || !voice.meta.id || voice.transcript.length === 0) return;
    // Debounce so we upsert ~2.5s after activity settles, plus a final upload
    // once endedAt is set (stop). Captures sessions even if never stopped/reloaded.
    const t = setTimeout(() => {
      void postDevLog({
        voice_session_id: voice.meta.id,
        agent: voice.target,
        started_at: voice.meta.startedAt,
        ended_at: voice.meta.endedAt,
        lines: voice.transcript,
      }, realtimeEndpoint || undefined);
    }, 2500);
    return () => clearTimeout(t);
  }, [devlog, voice.transcript, voice.meta, voice.target]);

  // Disarm roam on (re)start; a grace fallback arms it after 10s even if there is
  // nothing to narrate (idle agent, no history).
  useEffect(() => {
    if (voice.state !== 'live') { setRoamArmed(false); return; }
    setRoamArmed(false);
    const t = setTimeout(() => setRoamArmed(true), 10000);
    return () => clearTimeout(t);
  }, [voice.state]);
  // Arm as soon as the home agent has been narrated once (first 'voice' line).
  useEffect(() => {
    if (voice.state === 'live' && !roamArmed && voice.transcript.some((l) => l.role === 'voice')) {
      setRoamArmed(true);
    }
  }, [voice.state, roamArmed, voice.transcript]);

  // Decide the roam target: only when live + ambient + ARMED + the home agent is idle.
  useEffect(() => {
    if (voice.state !== 'live' || ambientMode !== 'ambient') { setRoamTarget(null); return; }
    if (!roamArmed) { setRoamTarget(null); return; } // home recap plays first
    const base = baseFocusRef.current;
    if (isActive(agentStates[base || ''])) { setRoamTarget(null); return; } // home is working → stay
    setRoamTarget((cur) => pickRoamTarget(agentStates, base, cur, roamScope));
  }, [voice.state, ambientMode, agentStates, roamScope, roamArmed]);

  // Only explicit operator navigation moves the real focus. Ambient roaming is
  // a separate narration source and must never redirect Talkback/Transparent.
  useEffect(() => {
    if (voice.state === 'live' && baseFocus && voice.target !== baseFocus) {
      voice.focusShift(baseFocus);
    }
  }, [voice, baseFocus]);

  useEffect(() => {
    if (voice.state === 'live') voice.ambientShift(roamTarget);
  }, [voice, roamTarget]);

  // Roam feed loop: while roaming, poll the background agent's /v2 and feed its
  // priming + deltas (it isn't rendered, so nothing else feeds it). The dedup
  // (last/primed) lives in a REF keyed by target so it SURVIVES effect re-runs —
  // the roam target flaps null↔X whenever the home agent flickers active/idle,
  // and a local `last` reset on every re-run re-fed the SAME message 4× (Alex
  // 2026-06-24 log vs_CloudV2_189080). Reset only on a real target change.
  useEffect(() => {
    if (!roamTarget || voice.state !== 'live') return;
    if (roamFeedRef.current.target !== roamTarget) {
      roamFeedRef.current = { target: roamTarget, last: '', primed: false };
    }
    let alive = true;
    const tick = async () => {
      try {
        const res = await api.sessions.v2(roamTarget, 200);
        if (!alive) return;
        const turns = res?.turns || [];
        const lastTurn = turns[turns.length - 1];
        if (!lastTurn) return;
        const st = roamFeedRef.current;
        if (!st.primed) {
          st.primed = true;
          const recent = turns.slice(-2).map((t) => turnNarrationText(t)).filter(Boolean).join('\n\n');
          if (recent) voice.feedAmbientHistory(`[Hintergrund-Agent ${roamTarget}]\n${recent}`, roamTarget);
          st.last = turnNarrationText(lastTurn, { inFlight: true });
          return;
        }
        const full = turnNarrationText(lastTurn, { inFlight: true });
        const delta = (st.last && full.startsWith(st.last)) ? full.slice(st.last.length) : full;
        st.last = full;
        if (delta.trim()) voice.feedAmbientMessage(delta, roamTarget);
      } catch { /* transient poll error — retry next tick */ }
    };
    void tick();
    const iv = setInterval(tick, ROAM_POLL_MS);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roamTarget, voice.state]);

  const value: VoiceContextValue = {
    ...voice,
    start,
    setBaseFocus,
    ambientMode,
    setAmbientMode,
    isRoaming: !!roamTarget,
    roamTarget,
    canUseVoice,
    runningModeLabel,
  };
  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export function useVoice(): VoiceContextValue {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within a VoiceProvider');
  return ctx;
}

/**
 * App-level voice layer: the audio sink (MUST be here, not in AgentDetail —
 * otherwise the <audio> element unmounts on navigation and the narrator goes
 * silent) plus a minimal always-on control pill. The pill is shown only when the
 * detailed AgentDetail voice-bar is NOT on screen (`miniBar`), so the operator
 * can always see + stop the narrator (and see who it's roaming on) from any view.
 */
export function GlobalVoiceLayer({ miniBar }: { miniBar: boolean }) {
  const voice = useVoice();
  const active = voice.state === 'live' || voice.state === 'connecting';
  // Sprechen per Leertaste (Alex 2026-08-09). Der eigentliche Sprechknopf ist
  // das Avatar-Video in der Seitenleiste — die gibt es in der Multi-Ansicht, im
  // Dashboard, im Verlauf und in Collab nicht. Seit #966 überlebt die Sitzung
  // den Ansichtswechsel, aber man konnte dort nicht mehr sprechen: Die Stimme
  // war ausgerechnet in der Übersicht stumm. Dieser Haken sitzt in der globalen
  // Schicht, weil die in JEDER Ansicht gerendert wird — dieselbe Stelle, an der
  // auch der Tonausgang hängt.
  const [keyHeld, setKeyHeld] = useState(false);
  const heldRef = useRef(false);
  const liveRef = useRef(false);
  liveRef.current = voice.state === 'live';

  const releasePtt = useCallback(() => {
    if (!heldRef.current) return;
    heldRef.current = false;
    setKeyHeld(false);
    voice.pushToTalk(false);
  }, [voice]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldStartPtt(e, liveRef.current)) return;
      // Erst hier — sonst würde ein Leerzeichen in einem Textfeld oder das
      // Auslösen eines fokussierten Knopfes verschluckt (CloudV2-Codex).
      e.preventDefault();
      if (heldRef.current) return;
      heldRef.current = true;
      setKeyHeld(true);
      voice.pushToTalk(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!shouldStopPtt(e, heldRef.current)) return;
      e.preventDefault();
      releasePtt();
    };
    // Fail-closed: Fenster verlassen, Tab versteckt, Fokus weg — in all diesen
    // Fällen kommt das `keyup` nie an, und das Mikrofon bliebe offen. Ein offen
    // gebliebenes Mikrofon ist der einzige Fehler, den diese Funktion machen
    // darf, also schließen wir bei jedem Zweifel.
    const onLeave = () => releasePtt();
    const onVisibility = () => { if (document.hidden) releasePtt(); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onLeave);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      // Beim Abbau der Schicht ebenfalls schließen — sonst überlebt ein offenes
      // Mikrofon den Wechsel, den es gerade nicht mehr mitbekommt.
      releasePtt();
    };
  }, [voice, releasePtt]);

  // Endet die Sitzung, während die Taste noch gehalten wird, gibt es kein
  // Gegenstück mehr zum Öffnen — Zustand hier zurücksetzen, damit die Pille
  // nicht „Aufnahme" behauptet.
  useEffect(() => {
    if (voice.state !== 'live') releasePtt();
  }, [voice.state, releasePtt]);

  return (
    <>
      <audio ref={voice.audioRef} hidden />
      {miniBar && active && (
        <div className={`voice-minibar voice-bar-${voice.state}`}>
          <span className="voice-minibar-dot" aria-hidden />
          {/* Der Hinweis ist die einzige Stelle, an der die Taste ueberhaupt
              entdeckt werden kann — in der Multi-Ansicht gibt es kein Video,
              an dem man sie ablesen koennte. */}
          <span className={`voice-minibar-ptt${keyHeld ? ' on' : ''}`}>
            {keyHeld ? 'Aufnahme' : 'Leertaste halten'}
          </span>
          {voice.runningModeLabel === 'Arcturian' && (
            <span
              className={`voice-minibar-context${voice.arcturianInforming ? ' on' : ''}`}
              title={voice.arcturianInforming
                ? 'Kontext wird eingespeist; Arcturian informiert über neue Updates des fokussierten Agenten.'
                : 'Kein laufender Agenten-Kontext wird eingespeist oder erneut berechnet.'}
            >
              {voice.arcturianInforming ? 'Updates an' : 'Updates aus'}
            </span>
          )}
          <span className="voice-minibar-label">
            {/* Der Modus stand hier fest verdrahtet als „AgentOS-Narrator" — die
                Pille behauptete das auch bei Talkback, Gespräch und Arcturian
                (Alex' Screenshot 118485: „ARC" gewählt, Pille sagt Narrator).
                Jetzt aus dem Modus der LAUFENDEN Sitzung, nicht aus einer
                Annahme. */}
            {voice.state === 'connecting'
              ? `${voice.runningModeLabel} verbindet…`
              : `${voice.runningModeLabel}${voice.target ? ` · ${voice.target}` : ''}${voice.isRoaming ? ' (ambient)' : ''}`}
          </span>
          <button type="button" className="voice-minibar-stop" onClick={voice.stop} title="Gespräch beenden">
            <Square size={11} />
          </button>
        </div>
      )}
    </>
  );
}
